import { useNavigate } from '@tanstack/react-router';
import { Calendar, CheckCircle2, ChevronRight, Clock, Inbox } from 'lucide-react';
import { Card } from '@/ui/primitives';
import { cn } from '@/lib/utils';
import { useMinhasTasks, useAtualizarTask } from '../hooks/useTasks';
import {
  PRIORIDADE_LABEL,
  STATUS_LABEL,
  type Task,
  type TaskStatus,
} from '../tasks-schemas';

type Bucket = 'atrasadas' | 'hoje' | 'amanha' | 'semana' | 'depois' | 'sem_prazo';

const BUCKET_LABEL: Record<Bucket, string> = {
  atrasadas: 'Atrasadas',
  hoje: 'Hoje',
  amanha: 'Amanhã',
  semana: 'Próximos 7 dias',
  depois: 'Mais tarde',
  sem_prazo: 'Sem prazo definido',
};

function diasAte(prazo: string | null | undefined): number | null {
  if (!prazo) return null;
  const d = new Date(prazo);
  if (isNaN(d.getTime())) return null;
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - hoje.getTime()) / 86400000);
}

function bucketOf(task: Task): Bucket {
  const dias = diasAte(task.prazo_em);
  if (dias == null) return 'sem_prazo';
  if (dias < 0) return 'atrasadas';
  if (dias === 0) return 'hoje';
  if (dias === 1) return 'amanha';
  if (dias <= 7) return 'semana';
  return 'depois';
}

function formatDateOnly(s: string | null | undefined) {
  if (!s) return null;
  try { return new Date(s).toLocaleDateString('pt-BR'); } catch { return s; }
}

export function MinhasTarefasView() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useMinhasTasks();
  const atualizar = useAtualizarTask();

  const tasks = data?.tasks ?? [];

  // Agrupa por bucket de urgência
  const grupos: Record<Bucket, Task[]> = {
    atrasadas: [], hoje: [], amanha: [], semana: [], depois: [], sem_prazo: [],
  };
  for (const t of tasks) {
    grupos[bucketOf(t)].push(t);
  }

  function onConcluir(t: Task) {
    atualizar.mutate({ taskId: t.id, input: { status: 'concluida' as TaskStatus } });
  }

  function onAbrirCard(oportunidadeId: number) {
    void navigate({ to: '/crm', search: { card: String(oportunidadeId) } as never });
  }

  if (isLoading) {
    return <p className="text-sm text-text-muted">carregando tarefas…</p>;
  }
  if (error) {
    return <p className="text-sm text-danger">erro ao carregar tarefas</p>;
  }
  if (tasks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3 text-center">
        <Inbox size={32} className="text-text-muted" strokeWidth={1.5} />
        <div>
          <h3 className="text-sm font-medium text-text">Sem tarefas pendentes</h3>
          <p className="text-[0.78rem] text-text-2 mt-1">
            Crie tarefas dentro dos cards de oportunidade pra acompanhar suas próximas ações aqui.
          </p>
        </div>
      </div>
    );
  }

  const buckets: Bucket[] = ['atrasadas', 'hoje', 'amanha', 'semana', 'depois', 'sem_prazo'];

  return (
    <div className="flex flex-col gap-5">
      {buckets.map((b) => {
        const items = grupos[b];
        if (items.length === 0) return null;
        const tone =
          b === 'atrasadas' ? 'text-[var(--danger)]'
          : b === 'hoje' ? 'text-[#a16207] dark:text-[#facc15]'
          : 'text-text-2';
        return (
          <section key={b} className="flex flex-col gap-2">
            <h3 className={cn('text-[0.78rem] font-semibold uppercase tracking-wide', tone)}>
              {BUCKET_LABEL[b]} <span className="text-text-muted font-normal">({items.length})</span>
            </h3>
            <ul className="flex flex-col gap-2">
              {items.map((t) => (
                <TaskCard
                  key={t.id}
                  task={t}
                  bucket={b}
                  onConcluir={() => onConcluir(t)}
                  onAbrir={() => onAbrirCard(t.oportunidade_id)}
                  disabled={atualizar.isPending}
                />
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

interface TaskCardProps {
  task: Task;
  bucket: Bucket;
  onConcluir: () => void;
  onAbrir: () => void;
  disabled?: boolean;
}

function TaskCard({ task, bucket, onConcluir, onAbrir, disabled }: TaskCardProps) {
  const dias = diasAte(task.prazo_em);
  const emAndamento = task.status === 'em_andamento';

  return (
    <Card className="p-3 flex items-start gap-3">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onConcluir(); }}
        disabled={disabled}
        className="mt-0.5 shrink-0 w-5 h-5 rounded border border-border2 hover:border-accent hover:bg-[rgba(217,119,87,0.08)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        title="Marcar como concluída"
        aria-label="Concluir"
      >
        {emAndamento && <Clock size={11} className="text-info" />}
      </button>
      <div className="flex-1 min-w-0 flex flex-col gap-1">
        <div className="flex items-start justify-between gap-2">
          <span className="text-sm font-medium text-text leading-tight">
            {task.titulo}
          </span>
          {task.prioridade && (
            <span
              className={cn(
                'shrink-0 inline-block px-1.5 py-0.5 rounded text-[0.65rem] font-semibold uppercase',
                task.prioridade === 'alta'
                  ? 'bg-[rgba(220,38,38,0.10)] text-[var(--danger)]'
                  : task.prioridade === 'media'
                  ? 'bg-[rgba(234,179,8,0.14)] text-[#a16207] dark:text-[#facc15]'
                  : 'bg-surface2 text-text-2',
              )}
            >
              {PRIORIDADE_LABEL[task.prioridade]}
            </span>
          )}
        </div>
        {task.descricao && (
          <p className="text-[0.78rem] text-text-2 line-clamp-2">{task.descricao}</p>
        )}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[0.72rem] text-text-muted">
          {task.prazo_em && (
            <span
              className={cn(
                'inline-flex items-center gap-1',
                bucket === 'atrasadas' && 'text-danger font-semibold',
                bucket === 'hoje' && 'text-warn font-semibold',
              )}
            >
              <Calendar size={11} />
              {formatDateOnly(task.prazo_em)}
              {bucket === 'atrasadas' && dias != null && ` (atrasada ${Math.abs(dias)}d)`}
              {bucket === 'hoje' && ' (hoje)'}
              {bucket === 'amanha' && ' (amanhã)'}
            </span>
          )}
          <span className="text-text-muted">{STATUS_LABEL[task.status]}</span>
        </div>
      </div>
      <button
        type="button"
        onClick={onAbrir}
        className="shrink-0 text-[0.78rem] text-accent hover:underline flex items-center gap-0.5"
        title="Abrir card da oportunidade"
      >
        <CheckCircle2 size={11} className="opacity-0" />
        Abrir card
        <ChevronRight size={12} />
      </button>
    </Card>
  );
}
